import { useState, useEffect } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddonBalanceDisplay } from "@/components/addons/AddonBalanceDisplay";
import { AddonCatalogCard } from "@/components/addons/AddonCatalogCard";
import { useAddonCatalog, usePostPurchaseHandler } from "@/hooks/addons";
import { Check, Book, Heart, CreditCard, ShoppingCart, User } from "lucide-react";
import { TeddyBear } from '../components/icons/TeddyBear';
import Navigation from "@/components/Navigation";

if (!import.meta.env.VITE_STRIPE_PUBLIC_KEY) {
  throw new Error('Missing required Stripe key: VITE_STRIPE_PUBLIC_KEY');
}
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const SubscribeForm = ({ selectedPlan }: { selectedPlan: 'monthly' | 'yearly' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const planDetails = {
    monthly: { price: '$6.99/month', amount: 6.99 },
    yearly: { price: '$69.99/year', amount: 69.99, savings: 'Save $13.89!' }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/home`,
      },
      redirect: 'if_required',
    });

    if (error) {
      toast({
        title: "Payment Failed",
        description: error.message,
        variant: "destructive",
      });
      setIsProcessing(false);
    } else {
      // Payment succeeded
      toast({
        title: "Welcome!",
        description: "Your subscription is now active.",
      });
      // Redirect to home page after short delay
      setTimeout(() => {
        window.location.href = '/home';
      }, 2000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement 
        options={{
          layout: "tabs",
        }}
      />
      <Button 
        type="submit" 
        className="w-full" 
        variant="gradient"
        size="lg"
        disabled={!stripe || isProcessing}
      >
        {isProcessing ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            Processing...
          </div>
        ) : (
          `Subscribe for ${planDetails[selectedPlan].price}`
        )}
      </Button>
    </form>
  );
};

export default function SubscribePage() {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [clientSecret, setClientSecret] = useState("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');

  // Add-ons data
  const { data: addonCatalogResponse } = useAddonCatalog();
  const { handlePostPurchase } = usePostPurchaseHandler();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user has lifetime subscription first
      if (user.subscriptionStatus === 'lifetime') {
        setHasActiveSubscription(true);
        return;
      }
      
      // Send plan parameter for monthly/yearly selection
      apiRequest("POST", "/api/create-subscription", { plan: selectedPlan })
        .then((res) => res.json())
        .then((data) => {
          console.log("Subscription response:", data);
          if (data.status === 'lifetime') {
            // Handle lifetime subscription response
            setHasActiveSubscription(true);
          } else if (data.clientSecret) {
            setClientSecret(data.clientSecret);
          } else {
            // If no clientSecret, user might already be subscribed
            console.log("No client secret - user might already be subscribed");
            setHasActiveSubscription(true);
          }
        })
        .catch((error) => {
          console.error("Failed to create subscription:", error);
          // Don't show error for lifetime members
          if (user.subscriptionStatus !== 'lifetime') {
            toast({
              title: "Error",
              description: `Failed to load subscription: ${error.message || 'Please try again.'}`,
              variant: "destructive",
            });
          }
        });
    }
  }, [isAuthenticated, user, toast, selectedPlan]);

  // Handle post-purchase URL parameters for add-ons
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    handlePostPurchase(searchParams);
  }, [handlePostPurchase]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="container mx-auto py-16 px-4 text-center">
          <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">Please sign in to subscribe</h1>
          <Button onClick={() => window.location.href = '/auth'} variant="gradient">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (hasActiveSubscription || (user && user.subscriptionStatus === 'lifetime')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-gradient-to-br from-purple-600 to-teal-600 rounded-2xl shadow-lg">
                  <CreditCard className="h-12 w-12 text-white" />
                </div>
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-4">
                Manage Your Subscription
              </h1>
              <p className="text-xl text-gray-700">
                You have an active SnuggleCrafters.com subscription
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-2">
              {/* Current Subscription */}
              <div className="lg:order-1">
                <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Current Subscription</span>
                  <Badge className={user?.subscriptionStatus === 'lifetime' 
                    ? "bg-gradient-to-r from-amber-600 to-purple-600 text-white" 
                    : "bg-gradient-to-r from-green-600 to-teal-600 text-white"}>
                    {user?.subscriptionStatus === 'lifetime' ? '🎉 Lifetime Access' : 'Active'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {user?.subscriptionStatus === 'lifetime' 
                    ? "You have lifetime access to all SnuggleCrafters.com features!"
                    : "Your subscription is currently active"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Unlimited story generation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium">AI-powered narration</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Story illustrations & stickers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Physical book ordering</span>
                  </div>
                </div>

                <div className="pt-6 border-t space-y-4">
                  {user?.subscriptionStatus === 'lifetime' ? (
                    <p className="text-sm text-gray-600">
                      You have lifetime access to all features - no recurring payments or billing management needed!
                    </p>
                  ) : (
                    <p className="text-sm text-gray-600">
                      Your subscription will automatically renew. You can manage your billing settings through Stripe.
                    </p>
                  )}
                  
                  <div className="space-y-2">
                    <Button 
                      variant="gradient" 
                      className="w-full text-white"
                      onClick={() => window.location.href = '/home'}
                    >
                      <Book className="h-4 w-4 mr-2" />
                      Continue to Stories
                    </Button>
                    
                    {user?.subscriptionStatus !== 'lifetime' && (
                      <>
                        <Button 
                          variant="gradient" 
                          className="w-full text-white"
                          onClick={async () => {
                            try {
                              const res = await apiRequest("POST", "/api/create-customer-portal-session");
                              const data = await res.json();
                              if (data.url) {
                                window.location.href = data.url;
                              } else {
                                throw new Error("No portal URL returned");
                              }
                            } catch (error) {
                              toast({
                                title: "Portal Not Configured",
                                description: "Please configure the customer portal in your Stripe dashboard. See STRIPE_SETUP_GUIDE.md for instructions.",
                                variant: "destructive",
                              });
                            }
                          }}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Manage Billing & Subscription
                        </Button>
                        
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Update payment method, download invoices, or cancel subscription
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
                </Card>
              </div>

              {/* Gift Subscription Section */}
              <div className="lg:order-2">
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-6 w-6 text-amber-600" />
                      Gift a Subscription
                    </CardTitle>
                    <CardDescription>
                      Share the magic of storytelling with someone special
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="text-center p-6 bg-white rounded-xl border border-amber-200">
                      <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TeddyBear className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Perfect Gift for Families</h3>
                      <p className="text-gray-600 mb-4">
                        Give the gift of personalized bedtime stories that create lasting memories
                      </p>
                      <p className="text-2xl font-bold text-amber-600 mb-2">Starting at $6.99/month</p>
                      <p className="text-sm text-gray-500">Recipient can manage their own subscription</p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-amber-600" />
                        <span className="text-sm">Digital gift card delivery</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-amber-600" />
                        <span className="text-sm">Personalized gift message</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Check className="h-5 w-5 text-amber-600" />
                        <span className="text-sm">Instant or scheduled delivery</span>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                      onClick={() => window.location.href = '/gift-subscription'}
                    >
                      <Heart className="h-4 w-4 mr-2" />
                      Purchase Gift Subscription
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      Gift subscriptions are delivered via email and can be redeemed immediately
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="container mx-auto py-16 px-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />
      
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-gradient-to-br from-purple-600 to-teal-600 rounded-2xl shadow-lg">
                <TeddyBear className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent mb-4">
              Continue Your Magical Journey
            </h1>
            <p className="text-xl text-gray-700">
              Unlock unlimited stories and premium features
            </p>
          </div>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Features */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TeddyBear className="h-6 w-6 text-primary" />
                  What's Included
                </CardTitle>
                <CardDescription>
                  Everything you need for magical storytelling
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Unlimited Stories</h4>
                    <p className="text-sm text-gray-600">Create as many personalized bedtime stories as you want</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-teal-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">AI-Powered Narration</h4>
                    <p className="text-sm text-gray-600">High-quality text-to-speech with natural voices</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Story Illustrations</h4>
                    <p className="text-sm text-gray-600">Beautiful AI-generated illustrations for every story</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">PDF Downloads</h4>
                    <p className="text-sm text-gray-600">Save stories as PDFs and coloring books</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Character Stickers</h4>
                    <p className="text-sm text-gray-600">Generate fun stickers of story characters</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Physical Books</h4>
                    <p className="text-sm text-gray-600">Order professionally printed story books</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Options */}
            <div className="space-y-6">
              {/* Plan Selection */}
              <Card>
                <CardHeader>
                  <CardTitle>Choose Your Plan</CardTitle>
                  <CardDescription>
                    Select the billing cycle that works best for you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedPlan} onValueChange={(value: 'monthly' | 'yearly') => setSelectedPlan(value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">
                        <div className="flex items-center justify-between w-full">
                          <span>Monthly - $6.99/month</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="yearly">
                        <div className="flex items-center justify-between w-full">
                          <span>Yearly - $69.99/year</span>
                          <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Save $13.89!</Badge>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Payment */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Subscribe Now</span>
                    <Badge className="bg-gradient-to-r from-primary to-secondary text-white px-3 py-1">
                      {selectedPlan === 'monthly' ? '$6.99/month' : '$69.99/year'}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {selectedPlan === 'yearly' ? (
                      <span className="text-green-600 font-medium">Save $13.89 with yearly billing! Cancel anytime.</span>
                    ) : (
                      "Cancel anytime. No long-term commitments."
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <SubscribeForm selectedPlan={selectedPlan} />
                  </Elements>
                  
                  <div className="mt-6 pt-6 border-t">
                    <p className="text-xs text-gray-500 text-center">
                      By subscribing, you agree to our Terms of Service and Privacy Policy. 
                      Your subscription will automatically renew at the selected rate until canceled.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Gift Subscription */}
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-6 w-6 text-amber-600" />
                    Gift a Subscription
                  </CardTitle>
                  <CardDescription>
                    Share the magic of storytelling with someone special
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-amber-200">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <TeddyBear className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Perfect Gift for Families</h3>
                    <p className="text-gray-600 text-sm mb-3">
                      Give the gift of personalized bedtime stories
                    </p>
                    <p className="text-xl font-bold text-amber-600">Starting at $6.99/month</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-amber-600" />
                      <span className="text-sm">Digital gift card delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-amber-600" />
                      <span className="text-sm">Personalized gift message</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-amber-600" />
                      <span className="text-sm">Instant delivery</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                    onClick={() => window.location.href = '/gift-subscription'}
                  >
                    <Heart className="h-4 w-4 mr-2" />
                    Purchase Gift Subscription
                  </Button>
                </CardContent>
              </Card>

              {/* Add-ons Section - Hidden for lifetime users */}
              {user?.subscriptionStatus !== 'lifetime' && (
                <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-teal-50" data-testid="subscribe-addons-section">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ShoppingCart className="h-6 w-6 text-purple-600" />
                      Enhance Your Experience
                    </CardTitle>
                    <CardDescription>
                      Add extra illustration credits and storage to your subscription
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Current Balances */}
                    <AddonBalanceDisplay
                      className="w-full"
                      showQuickTopUp={false}
                      data-testid="subscribe-addon-balance-display"
                    />

                    {/* Add-on Catalog */}
                    {addonCatalogResponse?.addons ? (
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-gray-700">Popular Add-ons</h4>
                        <div className="grid grid-cols-1 gap-3">
                          {addonCatalogResponse.addons
                            .filter(addon => addon.isActive)
                            .slice(0, 2) // Show only top 2 popular add-ons
                            .map((addon) => (
                              <div key={addon.id} className="p-3 bg-white rounded-lg border border-purple-200">
                                <AddonCatalogCard
                                  addon={addon}
                                  className="border-0 shadow-none bg-transparent p-0"
                                  data-testid={`subscribe-addon-catalog-card-${addon.sku}`}
                                />
                              </div>
                            ))}
                        </div>
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => window.location.href = '/profile?tab=addons'}
                          data-testid="subscribe-view-all-addons"
                        >
                          <ShoppingCart className="h-4 w-4 mr-2" />
                          View All Add-ons
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted-foreground">
                        <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Add-ons loading...</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Lifetime Benefits Showcase - Only for lifetime users */}
              {user?.subscriptionStatus === 'lifetime' && (
                <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-purple-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <span className="text-2xl">🎉</span>
                      Lifetime Benefits
                    </CardTitle>
                    <CardDescription>
                      Everything you need for unlimited storytelling
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3 mb-2">
                          <Check className="h-5 w-5 text-green-600" />
                          <span className="font-semibold">Unlimited Stories</span>
                        </div>
                        <p className="text-sm text-gray-600">Generate as many personalized bedtime stories as you want</p>
                      </div>
                      
                      <div className="p-4 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3 mb-2">
                          <Check className="h-5 w-5 text-green-600" />
                          <span className="font-semibold">Unlimited Illustrations</span>
                        </div>
                        <p className="text-sm text-gray-600">Beautiful AI-generated images for every story</p>
                      </div>
                      
                      <div className="p-4 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3 mb-2">
                          <Check className="h-5 w-5 text-green-600" />
                          <span className="font-semibold">Unlimited Storage</span>
                        </div>
                        <p className="text-sm text-gray-600">Save all your stories and memories forever</p>
                      </div>
                      
                      <div className="p-4 bg-white rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3 mb-2">
                          <Check className="h-5 w-5 text-green-600" />
                          <span className="font-semibold">All Premium Features</span>
                        </div>
                        <p className="text-sm text-gray-600">AI narration, physical books, and more</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 text-center">
                      <p className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                        Thank you for being a lifetime member! 💜
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}